import React from 'react'
import { Box, colors, Flex, Popper, space, Table, TableRow, TertiaryButton, TextInput, Tooltip, TooltipContainer, useTooltip } from '@workday/canvas-kit-react'
import SectionHeader from './SectionHeader'
import AlertCardContainer from './AlertCardContainer'
import IncidentCardContainer from './IncidentCardContainer'
import TimeDisplayOption from '../models/TimeDisplayOptions'
import { EmptyState } from '../../components/EmptyState'
import { AgencyModel } from '../../models/AgencyModel'
import { IncidentFeedModel } from '../../models/IncidentFeedModel'
import { IncidentModel } from '../../models/IncidentModel'
import { AlertModel } from '../../models/AlertModel'
import styled from '@emotion/styled'
import { caretLeftSmallIcon, caretRightSmallIcon } from '@workday/canvas-system-icons-web'
import { PanelSide } from '../RespondAppCore'
import { AdSense } from '../../components'

const StyledTextInput = styled(TextInput)`
  padding-left: 40px;    
  background: url('/images/search-icon.png') no-repeat 2px;
  background-size: 36px;
  margin-right: 6px;
`
const StyledSidePanelSwitch = styled('div')<{panelSide: PanelSide}>`
  position: absolute;
  top: calc(50vh);
  border-top-right-radius: ${props => props.panelSide === 'right' ? "0px" : "5px"};
  border-bottom-right-radius: ${props => props.panelSide === 'right' ? "0px" : "5px"};
  border-top-left-radius: ${props => props.panelSide === 'right' ? "5px" : "0px"};
  border-bottom-left-radius: ${props => props.panelSide === 'right' ? "5px" : "0px"};
  height: 60px;
  width: 12px;
  margin-left: ${props => props.panelSide === 'right' ? "-12px" : "-1px"};
  left: ${props => props.panelSide === 'right' ? "unset" : "450px"};
  background: white;
  overflow: hidden;
  border-top: 1px solid rgb(185, 192, 199);
  border-bottom: 1px solid rgb(185, 192, 199);
  border-left: ${props => props.panelSide === 'right' ? "1px solid rgb(185, 192, 199)" : "0px"};
  border-right: ${props => props.panelSide === 'right' ? "0px" : "1px solid rgb(185, 192, 199)"};
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }

  button {
    margin-left: -14px;
    height: 60px;

    .wd-icon-fill {
      fill: rgb(185, 192, 199);
    }

    &:focus {
      .wd-icon-fill {
        fill: rgb(185, 192, 199)!important;
      }
    }

    &:hover {
      .wd-icon-fill {
        fill: black!important;
      }
    }
  }
`

export type IncidentListProps = {
  showAds: boolean
  timeDisplayOption: TimeDisplayOption
  collapsedHeader: boolean
  agencies: AgencyModel[]
  feedData: IncidentFeedModel
  onSelectIncident: (incident: IncidentModel) => void
  search: string
  setSearch: (search: string) => void
  isSmallScreenWidth: boolean
  onShowUserLocation: (location: {lat:number, lng: number}) => void
  panelSide: PanelSide
  onTogglePanelSide: () => void
}

const IncidentList = ({
  showAds,
  timeDisplayOption,
  collapsedHeader,
  agencies,
  feedData,
  onSelectIncident,
  search,
  setSearch,
  isSmallScreenWidth,
  onShowUserLocation,
  panelSide,
  onTogglePanelSide
}: IncidentListProps) => {
  const alerts = feedData.alerts
  const active = feedData.active
  const recent = feedData.recent
  const width = isSmallScreenWidth ? '100%' : '450px'
  const {targetProps, popperProps, tooltipProps} = useTooltip()
  let adPlacementCounter = 0

  const agencyForAgencyID = (agencyID: string) => {
    return agencies.find(agency => agency.agencyID === agencyID)!
  }

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          
          onShowUserLocation(pos)
        }
      )
    }
  }

  return (
    <Box overflowY="auto" backgroundColor={colors.frenchVanilla100} width={width}>
      {!isSmallScreenWidth && 
        <>
          <StyledSidePanelSwitch panelSide={panelSide}>
            <TertiaryButton 
              {...targetProps}
              onClick={()=> onTogglePanelSide()}
              icon={panelSide === 'right' ? caretLeftSmallIcon : caretRightSmallIcon} />
          </StyledSidePanelSwitch>
          <Popper placement="top" {...popperProps}>
            <TooltipContainer {...tooltipProps}>
              {panelSide === 'right' ? "Toggle List to Left" : "Toggle List to Right"}
            </TooltipContainer>
          </Popper>
        </>
      }
      {!collapsedHeader && 
        <Box padding={space.xs}>
          <Flex>
            <StyledTextInput
              value={search}
              onChange={(event: any) => setSearch(event.currentTarget.value)}
              grow
              placeholder="Filter by address, units, or name"
            />
            <Tooltip title="View My Location">
              <TertiaryButton as={'a'} onClick={getUserLocation}>
                <img src="/images/my-location.svg" width="24px" />
              </TertiaryButton>
            </Tooltip>
          </Flex>
        </Box>
      }
      {feedData.hasData() ? (
        <Table style={{ borderLeft: `1px solid ${colors.soap600}`, borderRight: `1px solid ${colors.soap600}` }}>
          <tbody>
            {alerts.length > 0 && <SectionHeader title="Alerts" count={alerts.length} />}
            {alerts.map((alert: AlertModel) => (
              <TableRow onClick={event => {}} key={alert.recordID}>
                <td style={{ border: 0, padding: 0, margin: 0 }}>
                  <AlertCardContainer
                    alert={alert}
                    agency={agencyForAgencyID(alert.agencyID)}
                    timeDisplayOption={timeDisplayOption}
                  />
                </td>
              </TableRow>
            ))}
            {active.length > 0 && <SectionHeader title="Active" count={active.length} />}
            {active.map((incident: IncidentModel) => {
              const agency = agencyForAgencyID(incident.agencyID)
              adPlacementCounter++

              return [
                <TableRow key={incident.recordID}
                  style={{cursor: 'pointer'}} 
                  onClick={event => onSelectIncident(incident)}
                >
                  <td style={{ border: 0, padding: 0, margin: 0 }}>
                    <IncidentCardContainer incident={incident} showAgencyTitle={agencies.length !== 1} agency={agency} timeDisplayOption={timeDisplayOption} />
                  </td>
                </TableRow>,
                showAds && getAdListItem(adPlacementCounter)
              ]
            })}
            {recent.length > 0 && <SectionHeader title="Recent" count={recent.length} />}
            {recent.map((incident: IncidentModel) => {
              const agency = agencyForAgencyID(incident.agencyID)
              adPlacementCounter++
              
              return [
                <TableRow  key={incident.recordID}
                  style={{cursor: 'pointer'}} 
                  onClick={event => onSelectIncident(incident)}
                >
                  <td style={{ border: 0, padding: 0, margin: 0 }}>
                    <IncidentCardContainer incident={incident} agency={agency} showAgencyTitle={agencies.length !== 1} timeDisplayOption={timeDisplayOption} />
                  </td>
                </TableRow>,
                showAds && getAdListItem(adPlacementCounter)
              ]
            })}
          </tbody>
        </Table>
      ) : (
        <EmptyState>
          <EmptyState.Body>No Recent Incidents</EmptyState.Body>
        </EmptyState>
      )}
    </Box>
  )
}

const getAdListItem = (counter: number) => {
  return (counter === 3 || counter > 5 && (counter - 3) % 5 === 0) && 
    <TableRow key={`ad-${counter}`}>
      <td style={{ border: 0, padding: 0, margin: 0, height: 'unset' }}>
        <AdSense
          adClient="ca-pub-9937384565543226"
          adSlot="2902804358"
          layout='-hd-z+46-6k+4d'
        />
      </td>
    </TableRow>
}

export default IncidentList
